import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { MapPin, Phone, Mail, Clock, MessageCircle, Lock } from "lucide-react";

export const Route = createFileRoute("/contacto")({
  head: () => ({
    meta: [
      { title: "Contacto — Estudio Libertad E.A.E.S. | Asunción" },
      {
        name: "description",
        content:
          "Agende una consulta privada con el Estudio Libertad E.A.E.S. · Consultores y Abogados en Asunción, Paraguay. Atención bajo cita previa.",
      },
      { property: "og:title", content: "Contacto — Estudio Libertad E.A.E.S." },
      {
        property: "og:description",
        content: "Agende una consulta confidencial en nuestro estudio en Asunción.",
      },
    ],
  }),
  component: ContactoPage,
});

const WHATSAPP = "595994471947";

function ContactoPage() {
  const [sent, setSent] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const nombre = (data.get("nombre") as string) || "";
    const asunto = (data.get("asunto") as string) || "";
    const mensaje = (data.get("mensaje") as string) || "";
    const text = encodeURIComponent(
      `Hola Estudio Libertad, soy ${nombre}. Asunto: ${asunto}. ${mensaje}`,
    );
    window.open(`https://wa.me/${WHATSAPP}?text=${text}`, "_blank");
    setSent(true);
    form.reset();
  };

  return (
    <>
      <section className="bg-navy-900 py-24 text-stone-100 md:py-32">
        <div className="container-elegant text-center">
          <span className="text-[10px] font-semibold tracking-[0.4em] uppercase text-gold-400">
            Contacto
          </span>
          <h1 className="mx-auto mt-6 max-w-3xl font-serif text-4xl leading-tight md:text-6xl">
            Una consulta privada,{" "}
            <span className="italic text-gold-400">absolutamente confidencial.</span>
          </h1>
          <div className="mx-auto mt-8 h-px w-16 bg-gold-500" />
          <p className="mx-auto mt-8 max-w-2xl text-stone-100/70">
            Atendemos exclusivamente bajo cita previa. Toda comunicación está
            protegida por el secreto profesional.
          </p>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="container-elegant grid gap-12 lg:grid-cols-5">
          {/* INFO */}
          <div className="space-y-10 lg:col-span-2">
            <div>
              <h2 className="font-serif text-3xl text-navy-900">Datos de contacto</h2>
              <div className="mt-4 h-px w-12 bg-gold-500" />
            </div>

            <ul className="space-y-7">
              <ContactItem icon={MapPin} title="Estudio Jurídico">
                Antequera 1026 casi Tte. Fariña<br />
                Asunción, Paraguay<br />
                <span className="text-navy-900/55">Atención bajo cita previa</span>
              </ContactItem>
              <ContactItem icon={Phone} title="Teléfono">
                <a href="tel:+595994471947" className="hover:text-gold-500">
                  +595 994 471 947
                </a>
              </ContactItem>
              <ContactItem icon={Mail} title="Correo Electrónico">
                <a href="mailto:contacto@estudiolibertad.com.py" className="hover:text-gold-500">
                  contacto@estudiolibertad.com.py
                </a>
              </ContactItem>
              <ContactItem icon={MessageCircle} title="WhatsApp Directo">
                <a
                  href={`https://wa.me/${WHATSAPP}`}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-gold-500"
                >
                  +595 994 471 947
                </a>
              </ContactItem>
              <ContactItem icon={Clock} title="Horario">
                Lunes a Viernes<br />
                08:30 — 18:00
              </ContactItem>
            </ul>

            <div className="flex items-start gap-3 border-l-2 border-gold-500 bg-stone-100 p-6">
              <Lock className="mt-0.5 h-5 w-5 shrink-0 text-gold-500" />
              <p className="text-sm leading-relaxed text-navy-900/75">
                Su información está protegida por el <strong>secreto profesional</strong>.
                No compartimos datos con terceros bajo ninguna circunstancia.
              </p>
            </div>
          </div>

          {/* FORM */}
          <div className="lg:col-span-3">
            <div className="border border-navy-900/10 bg-white p-8 md:p-12">
              <h2 className="font-serif text-3xl text-navy-900">
                Solicitar consulta
              </h2>
              <p className="mt-2 text-sm text-navy-900/60">
                Le contactaremos a la brevedad posible.
              </p>
              <div className="mt-6 h-px w-12 bg-gold-500" />

              {sent && (
                <div className="mt-8 border border-gold-500/40 bg-gold-500/5 p-5 text-sm text-navy-900">
                  Su mensaje fue redirigido a WhatsApp para envío inmediato.
                  Gracias por su confianza.
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-10 space-y-6">
                <Field label="Nombre completo" name="nombre" required />
                <div className="grid gap-6 md:grid-cols-2">
                  <Field label="Teléfono" name="telefono" type="tel" required />
                  <Field label="Correo electrónico" name="email" type="email" />
                </div>
                <Field label="Asunto legal" name="asunto" required />
                <div>
                  <label className="block text-[10px] font-semibold tracking-[0.25em] uppercase text-navy-900/60">
                    Descripción breve
                  </label>
                  <textarea
                    name="mensaje"
                    rows={5}
                    required
                    className="mt-3 w-full border-b border-navy-900/15 bg-transparent py-3 text-navy-900 outline-none transition-colors focus:border-gold-500"
                  />
                </div>

                <button
                  type="submit"
                  className="mt-4 w-full bg-navy-900 px-8 py-5 text-[11px] font-semibold tracking-[0.25em] uppercase text-stone-100 transition-colors hover:bg-gold-500"
                >
                  Enviar consulta confidencial
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function ContactItem({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <li className="flex gap-5">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center border border-gold-500/40 text-gold-500">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <h3 className="text-[10px] font-semibold tracking-[0.25em] uppercase text-navy-900/55">
          {title}
        </h3>
        <p className="mt-2 text-navy-900">{children}</p>
      </div>
    </li>
  );
}

function Field({
  label,
  name,
  type = "text",
  required,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label className="block text-[10px] font-semibold tracking-[0.25em] uppercase text-navy-900/60">
        {label}
      </label>
      <input
        type={type}
        name={name}
        required={required}
        className="mt-3 w-full border-b border-navy-900/15 bg-transparent py-3 text-navy-900 outline-none transition-colors focus:border-gold-500"
      />
    </div>
  );
}